/**
 * `deriveShippingMode` — pure mode-deriver for a single-seller cart slice.
 *
 * Why a pure helper: branching the checkout UI on a `shipping_mode` value
 * makes the discriminator a domain decision (which carrier pipeline runs).
 * `hex` rule: domain in `lib/`. The component just reads the verdict.
 *
 * Single-seller invariant (PLAN §J.5): one CheckoutIntent = one seller. The
 * cart drawer renders N seller groups, each with its own Pay button → each
 * checkout sees one seller's items. So `deriveShippingMode` ALWAYS evaluates
 * the homogeneous case in production. Defensive heterogeneous handling
 * (different modes within one group) returns `'legacy'` + warns, so a future
 * data drift can never silently route a mixed cart through the live-rate
 * sub-flow without parcel dims for some items.
 *
 * Default policy:
 *  - items without `shippingMode` set (cart route not yet extended for
 *    WS4.4) → treated as `'legacy'`. Today's prod 100% legacy path is
 *    byte-for-byte unchanged when the data carries no mode information.
 *  - all items `'live_rate'` → `'live_rate'`.
 *  - any mix → `'legacy'` + `console.warn` (data bug; degrade safely).
 *
 * @see .tondocs/shippo/PLAN.md §D WS4 (per-seller routing by shipping_mode)
 * @see .tondocs/shippo/PLAN.md §J.5 (single-seller invariant)
 * @see .tondocs/skills/hex.md §1 (pure domain layer)
 * @see .tondocs/skills/type-driven-design.md §1 (discriminated union, single
 *      canonical declaration shared with `lib/shipping/types.ts`)
 */

/**
 * `ShippingMode` mirrors `listings.shipping_mode` post-K.6 rename
 * (PLAN §K.6, S3). Kept as a local string-literal union here so this domain
 * helper doesn't pull the broader `ShippingConfig` discriminated union from
 * `lib/shipping/types.ts` (which carries `methods`/`parcel`/`provider`
 * payloads the checkout doesn't read at this layer).
 */
export type ShippingMode = "legacy" | "live_rate";

export interface ShippingModeInput {
  // Optional (not just `| undefined`) so this interface accepts any subtype
  // of `CartItemData` directly — its `shippingMode?:` is property-optional.
  // The reducer below already treats absence as `'legacy'`.
  shippingMode?: ShippingMode | null;
}

export function deriveShippingMode(
  items: ReadonlyArray<ShippingModeInput>,
): ShippingMode {
  if (items.length === 0) return "legacy";

  let liveRate = 0;
  let legacy = 0;

  for (const item of items) {
    if (item.shippingMode === "live_rate") liveRate += 1;
    else legacy += 1;
  }

  if (liveRate > 0 && legacy > 0) {
    // Single-seller-invariant assumption violated; this should never happen
    // in a healthy data path (WS3 publish-time invariants pin one mode per
    // seller). Defensive legacy keeps checkout working.
    console.warn(
      "[deriveShippingMode] Heterogeneous shipping modes in a single checkout — falling back to legacy",
      { liveRate, legacy },
    );
    return "legacy";
  }

  return liveRate > 0 ? "live_rate" : "legacy";
}
